import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { TBankAccount } from '../../types'
import { useParams } from 'react-router-dom'
import { getBankAccountDetails, sendDeposit } from '../../api'
import { setLoading } from 'redux/store/common/slice'
import { useDispatch, useSelector } from 'react-redux'
import { getVendorInfo } from 'redux/store/vendorInfo/getters'
import {
  getAccountPurposeOptions,
  getHospitalData
} from 'features/HealthSystemDetails/HospitalsTable/api'
import { NOTIFICATIONS } from 'constants/txt'
import { notification } from 'components/Notification'
import { getUser } from 'redux/store/user/getters'
import {
  ACTIONS,
  bankAccountSubject,
  defineAbility,
  SUBJECTS
} from 'features/BankAccount/Abilities'
import { TOption } from 'components/Select/types'
import { formatOptionIdValue } from 'helper/optionsFormatters'
import {
  getHealthSystemData,
  getHealthSystemOutsideCommunityData
} from 'features/MyCommunity/MyOrganizationCommunity/api'
import { ROLES } from 'features/Permission'
import { getCommunityDetails } from 'features/MyCommunity/Communities/CommunityDetails/api'

type ContextProps = {
  state: {
    details: TBankAccount | null
    moovBusinessAccountId: string | null
    canEditBankAccountDetails: boolean
    canSendDeposit: boolean
    canVerifyDeposit: boolean
    canResendDeposit: boolean
    canChangeAccountPurpose: boolean
    canSetAsDefaultReceivableAccount: boolean
    entityName?: string | null
    accountPurposeOptions: TOption[]
  }
  actions: {
    getDetails: () => Promise<void>
    onSendDeposit: () => Promise<void>
  }
}

const BankAccountDetailsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const BankAccountDetailsContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [details, setDetails] = useState<TBankAccount | null>(null)
  const [entityMoovBusinessAccountId, setEntityMoovBusinessAccountId] =
    useState<string | null>(null)
  const [entityName, setEntityName] = useState<string | null>(null)
  const [accountPurposeOptions, setAccountPurposeOptions] = useState<TOption[]>(
    []
  )
  const { id, hsId, hospitalId } = useParams()
  const dispatch = useDispatch()
  const vendorInfo = useSelector(getVendorInfo)
  const user = useSelector(getUser)

  const ability = useMemo(() => defineAbility(user), [user])

  const moovBusinessAccountId = useMemo(
    () =>
      entityMoovBusinessAccountId ?? vendorInfo.moov_business_account ?? null,
    [entityMoovBusinessAccountId, vendorInfo.moov_business_account]
  )

  useEffect(() => {
    if (!user.uuid) return
    if (user.role === ROLES.COMMUNITY_PRESIDENT && !!user.community) {
      getCommunityDetails(user.community, {})
        .then((resp) => {
          setEntityMoovBusinessAccountId(
            resp.data.moov_business_account ?? null
          )
          setEntityName(resp.data.name ?? null)
        })
        .catch((error) => {
          console.error(error)
        })
    } else if (user.community && hsId && hospitalId) {
      getHospitalData(user.community, hsId, hospitalId)
        .then((resp) => {
          setEntityMoovBusinessAccountId(
            resp.data.moov_business_account ?? null
          )
          setEntityName(resp.data.name ?? null)
        })
        .catch((error) => {
          console.error(error)
        })
    } else if (user.community && hsId) {
      getHealthSystemData(user.community, hsId)
        .then((resp) => {
          setEntityMoovBusinessAccountId(
            resp.data.moov_business_account ?? null
          )
          setEntityName(resp.data.name ?? null)
        })
        .catch((error) => {
          console.error(error)
        })
    } else if (user.outside_community && user.health_system && !hsId) {
      getHealthSystemOutsideCommunityData(user.health_system)
        .then((resp) => {
          setEntityMoovBusinessAccountId(
            resp.data.moov_business_account ?? null
          )
          setEntityName(resp.data.name ?? null)
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }, [
    hsId,
    hospitalId,
    user.role,
    user.community,
    user.uuid,
    user.outside_community,
    user.health_system
  ])

  const canEditBankAccountDetails = ability.can(
    ACTIONS.EDIT,
    SUBJECTS.BANK_ACCOUNT_DETAILS
  )

  const canSendDeposit = useMemo(
    () =>
      ability.can(ACTIONS.SEND, bankAccountSubject(SUBJECTS.DEPOSIT, details)),
    [ability, details]
  )
  const canVerifyDeposit = useMemo(
    () =>
      ability.can(
        ACTIONS.VERIFY,
        bankAccountSubject(SUBJECTS.DEPOSIT, details)
      ),
    [ability, details]
  )

  const canResendDeposit = useMemo(
    () =>
      ability.can(
        ACTIONS.RESEND,
        bankAccountSubject(SUBJECTS.DEPOSIT, details)
      ),
    [ability, details]
  )

  const canChangeAccountPurpose = useMemo(
    () =>
      ability.can(
        ACTIONS.EDIT,
        bankAccountSubject(SUBJECTS.ACCOUNT_PURPOSE, details)
      ),
    [ability, details]
  )

  const canSetAsDefaultReceivableAccount = useMemo(
    () =>
      ability.can(
        ACTIONS.SET_AS_DEFAULT,
        bankAccountSubject(SUBJECTS.BANK_ACCOUNT_DETAILS, details)
      ),
    [ability, details]
  )

  const fetchDetails = useCallback(async () => {
    if (!id || !moovBusinessAccountId) return
    try {
      dispatch(setLoading(true))
      const _details = await getBankAccountDetails(moovBusinessAccountId, id)
      setDetails(_details.data)
      if (moovBusinessAccountId && id && !accountPurposeOptions.length) {
        getAccountPurposeOptions({
          businessAccountId: moovBusinessAccountId,
          bankAccountId: id
        }).then((resp) => {
          setAccountPurposeOptions(resp.data.results.map(formatOptionIdValue))
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setLoading(false))
    }
  }, [accountPurposeOptions.length, dispatch, id, moovBusinessAccountId])

  const onSendDeposit = useCallback(async () => {
    if (!id || !moovBusinessAccountId) return
    try {
      dispatch(setLoading(true))
      await sendDeposit(moovBusinessAccountId, id)
      fetchDetails()
    } catch (error) {
      console.error(error)
      notification.error({
        message: (
          <span>
            {NOTIFICATIONS.DEPOSIT_SEND_ERROR}{' '}
            <span className="text-red">in a few minutes.</span>
          </span>
        )
      })
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, fetchDetails, id, moovBusinessAccountId])

  useEffect(() => {
    fetchDetails()
  }, [fetchDetails])

  const context = useMemo(
    () => ({
      state: {
        details,
        moovBusinessAccountId,
        canEditBankAccountDetails,
        canSendDeposit,
        canVerifyDeposit,
        canResendDeposit,
        canChangeAccountPurpose,
        canSetAsDefaultReceivableAccount,
        entityName,
        accountPurposeOptions
      },
      actions: {
        getDetails: fetchDetails,
        onSendDeposit
      }
    }),
    [
      details,
      moovBusinessAccountId,
      fetchDetails,
      onSendDeposit,
      canEditBankAccountDetails,
      canSendDeposit,
      canVerifyDeposit,
      canResendDeposit,
      canChangeAccountPurpose,
      entityName,
      accountPurposeOptions,
      canSetAsDefaultReceivableAccount
    ]
  )

  return (
    <BankAccountDetailsContext.Provider value={context}>
      {children}
    </BankAccountDetailsContext.Provider>
  )
}

export const useBankAccountDetailsContext = () =>
  useContext(BankAccountDetailsContext)

export default BankAccountDetailsContextProvider
